import { useParams } from "react-router-dom";
import { useState, useEffect, useRef } from 'react';
import { AxiosError } from 'axios';
import SBLogo from './sblogo.svg';
import styles from './Cashier.module.scss';
import { Button } from "@uicomponents";
import { } from '../../../../packages/sdk/src/index';
import { Destroyable, PushNamespace } from '../../../../packages/sdk/src/types';

// renderableImpl dynamically allows us to switch between loading the Push SDK from the imported module
// in development and from the window object / CDN version in production
// import { PushCash } from '../../../../packages/sdk/src/index';
// const renderableImpl : PushNamespace = process.env.NODE_ENV === 'development' ? {"Button": PushCash} : window.PushCash;
const renderableImpl : PushNamespace = window.PushCash;

import getAxios from './../lib/axios/axios';

enum Direction {
    IN = "cash_in",
    OUT = "cash_out",
}

function Cashier() {
  const [amount, setAmount] = useState<number | undefined>();
  const [amountDisplay, setAmountDisplay] = useState<string>("");
  const [direction, setDirection] = useState<Direction | undefined>(undefined);
  const { auth_code } = useParams();
  const [balance, setBalance] = useState<number>(0);

  const [error, setError] = useState<string | null>();
  const pushCashButtonRef = useRef<Destroyable | null>(null);
  
  const axios = getAxios();

  // load balance
  useEffect(() => {

    const loadBalance = async () => {
      try {
        const response = await axios.post("/demo/users/get_balance", { 
          auth_code
        })
        setBalance(response.data['balance'])
      } catch (error: any) {
        if (error instanceof AxiosError && error.response)  {
          setError(error.response.data);
          if (error.response.status >= 500) {
            console.error(error);
          }
        }
        else {
          setError(error.toString());
          console.error(error);
        }
      }
    };

    loadBalance();
  }, []);

  // load limits
  useEffect(() => {

    const loadLimits = async () => {
      try {
        const response = await axios.post("/demo/users/get_balance", { 
          auth_code
        })
      } catch (error: any) {
        if (error instanceof AxiosError && error.response)  {
          setError(error.response.data);
          if (error.response.status >= 500) {
            console.error(error);
          }
        }
        else {
          setError(error.toString());
          console.error(error);
        }
      }
    };

    loadLimits();
  }, [balance]);

  useEffect(() => {    
    const createIntent = async () => {
      // call your server to invoke the payment intent create endpoint and return the payment intent ID to the SDK
      try {
        const response = await axios.post("/demo/intents", { 
          "amount": amount! * 100,
          "direction": direction,
          "auth_code": auth_code
        })
        return response.data['url']
      }
      catch (error: any) {
        if (error instanceof AxiosError && error.response)  {
          setError(error.response.data);
          if (error.response.status >= 500) {
            console.error(error);
          }
        }
        else {
          setError(error.toString());
          console.error(error);
        }
      }
    }

    if (pushCashButtonRef.current === null) {
      pushCashButtonRef.current = renderableImpl.Button({
        selector: '#pushcash-button',
        style: {
          color: 'royal',
          height: 50,
          disabled: true
        },
      }).render();
      pushCashButtonRef.current.setCallback(createIntent);
    } else {
      pushCashButtonRef.current.setCallback(createIntent)
    }

    pushCashButtonRef.current.setDisabled(amount === undefined);

  }, [amount, direction]);

  // set focus on click - kinda hacky, but basically we just
  // have a value that toggles from true / false / true / false
  // so that the useEffect hook will run each time and set focus on the input
  const [click, setClick] = useState<boolean>(false);
  useEffect(() => {
    document.getElementById("currency-input")?.focus();
  }, [click]);

  if (error) {
    return (
      <div className="App">
        <div className="cta">
          <h2>An error occurred</h2>
          <p>Details: <code>{error}</code></p>
        </div>
      </div>
    );
  }


  var currencyInput : JSX.Element;
  if (direction === undefined) {
    currencyInput = (
      <div className={styles['currency-container']}>
        <p className={styles['discriminator']}>Balance</p>
        <span className={styles['currency']}><span>${(balance / 100).toLocaleString()}</span></span>
      </div>
    )
  } else {

    const handleAmountChange = (event : any) => {
      const newAmount = event.target.value;
      // Remove commas from the input and parse it as a number
      const numericAmount = parseFloat(newAmount.replace(/,/g, ''));

      // Limit inputs to no more than $10,000 to avoid overflow from the input element
      if (direction === Direction.IN && numericAmount > 10000) {
        return
      }

      // if direction is OUT, limit to less than balance
      if (direction === Direction.OUT && numericAmount > (balance / 100)) {
        return
      }

      setAmountDisplay(newAmount);

      // Check if the parsed numericAmount is a valid number
      if (!isNaN(numericAmount)) {
        // Set "amount" to the parsed value
        setAmount(numericAmount);
        setAmountDisplay(numericAmount.toLocaleString());
      } else {
        setAmount(undefined);
        setAmountDisplay("");
      }
    }

    console.log(amount);

    function computeStringWidth(inputString: string | undefined) {
      // inputString is undefined, means we are just rendering the placeholder for the input which is zero
      if (!inputString) {
        inputString = '0'
      }

      // Define a map of characters to the width in pixels
      // for glyphs from the Instrument Sans font type set at 50px font-size
      const charValues: { [key: string]: number } = {
        '0': 34,
        '1': 20,
        '2': 28,
        '3': 29,
        '4': 30,
        '5': 29,
        '6': 30,
        '7': 27,
        '8': 30,
        '9': 31,
      };

      // kernedValues represent the width of the digit displayed with a comma
      const kernedValues: { [key: string]: number } = {
        '0': 43,
        '1': 30,
        '2': 40,
        '3': 40,
        '4': 41,
        '5': 40,
        '6': 41,
        '7': 34,
        '8': 40,
        '9': 40,
      };

      let sum = 0;
      for (var i = inputString.length - 1; i >= 0;) {
        const char = inputString[i];
        if (char != ',') {
          sum += charValues[char];
          i -= 1;
        } else if (char == ',') {
          const nextChar = inputString[i - 1];
          sum += kernedValues[nextChar];
          i -= 2;
        } else {
          throw new Error('Invalid character in input string');
        }
      }
      return sum
    }

    currencyInput = (
      <div className={styles['currency-container']}>
        <div>
          <p className={styles['discriminator']}>{direction === Direction.IN ? "Add" : "Withdraw"}</p>
          <span className={styles['currency']}>
            <span>$</span>
            <input
              id="currency-input"
              style={{width: computeStringWidth(amountDisplay) + 'px'}}
              className={styles['currency-input']}
              type="text"
              value={amountDisplay}
              placeholder="0"
              onChange={handleAmountChange}>
            </input>
            <span className={(!amount ? styles['zero-amount'] : "")}>.00</span>
          </span>
        </div>
        <div>
          <p className={styles['discriminator']}>Balance</p>
          <span className={styles['currency']}><h4>${(balance / 100).toLocaleString()}</h4></span>
        </div>
      </div>
    )
  }

  return (
    <div className="App">
      <div className="header">
        <img src={SBLogo} alt='Logo' />
        <h4 className={styles['header-text']}>Sportsbook Cashier</h4>
      </div>
      <div className="content">
        <div className="flex-row flex-gap">
          <Button type="secondary" selected={direction === Direction.OUT} onClick={() => { setDirection(Direction.OUT); setClick(!click)} } text="Withdraw" disabled={balance === 0} />
          <Button type="secondary" selected={direction === Direction.IN} onClick={() => { setDirection(Direction.IN); setClick(!click)} } text="Deposit" />
        </div>
        {currencyInput}
      </div>
      <div className="footer">
        <div className={styles['button-container']}>
          <div id="pushcash-button">
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cashier;
